import { ReactNode } from 'react';
import { Flex, theme } from 'antd';
import { CheckIcon } from '@phosphor-icons/react';
import type { GlobalToken } from 'antd';
import styled from 'styled-components';
import { radius } from '../../theme/radius';

export type ChoiceCardProps = {
  /** Option title */
  label: ReactNode;
  /** Supporting text under the title */
  description?: ReactNode;
  /** Whether this option is the current pick */
  selected?: boolean;
  /** Corner mark shown only while selected */
  badge?: ReactNode;
  /**
   * Fixed-size check circle beside the label, empty until selected. Takes the
   * same space either way, so narrow cards don't reflow on selection.
   */
  showCheckmark?: boolean;
  onClick?: () => void;
};

/**
 * Selectable option card for single-choice questions.
 * Colors follow the nearest ConfigProvider, so a page can re-tint it.
 *
 * @example
 * ```tsx
 * <ChoiceCard
 *   label="Rich with examples"
 *   description="I use lots of examples to make it land."
 *   selected={value === 'rich'}
 *   badge="This one"
 *   onClick={() => setValue('rich')}
 * />
 * ```
 */
export const ChoiceCard = ({
  label,
  description,
  selected = false,
  badge,
  showCheckmark = false,
  onClick,
}: ChoiceCardProps) => {
  const { token } = theme.useToken();

  return (
    <StyledCard
      type="button"
      aria-pressed={selected}
      $token={token}
      $selected={selected}
      onClick={onClick}
    >
      {selected && badge ? (
        <StyledBadge $token={token}>{badge}</StyledBadge>
      ) : null}
      {showCheckmark ? (
        <Flex justify="space-between" align="flex-start" gap={8}>
          <StyledLabel $token={token}>{label}</StyledLabel>
          <StyledCheck $token={token} $selected={selected} aria-hidden>
            {selected ? <CheckIcon size={12} weight="bold" /> : null}
          </StyledCheck>
        </Flex>
      ) : (
        <StyledLabel $token={token}>{label}</StyledLabel>
      )}
      {description ? (
        <StyledDescription $token={token}>{description}</StyledDescription>
      ) : null}
    </StyledCard>
  );
};

export default ChoiceCard;

// ─── Styled components ───

// Column flex so content sits at the top when a row stretches cards to equal
// height; a plain button would centre it vertically.
const StyledCard = styled.button<{ $token: GlobalToken; $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  text-align: left;
  padding: 17px 18px;
  border-radius: ${radius.box}px;
  cursor: pointer;
  transition: 0.15s;
  background: ${({ $token, $selected }) =>
    $selected ? $token.colorPrimaryBg : $token.colorBgContainer};
  border: 1.5px solid
    ${({ $token, $selected }) =>
      $selected ? $token.colorPrimary : $token.colorBorder};

  &:hover {
    border-color: ${({ $token }) => $token.colorPrimary};
    box-shadow: ${({ $token }) => $token.boxShadowTertiary};
  }

  &:focus-visible {
    outline: 2px solid ${({ $token }) => $token.colorPrimary};
    outline-offset: 2px;
  }
`;

const StyledBadge = styled.span<{ $token: GlobalToken }>`
  position: absolute;
  top: 12px;
  right: 14px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${({ $token }) => $token.colorPrimary};
`;

// A plain span, not antd Flex: Flex hides itself when empty (:empty), which
// would drop the unselected circle.
const StyledCheck = styled.span<{ $token: GlobalToken; $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: ${radius.circle};
  color: ${({ $token }) => $token.colorWhite};
  background: ${({ $token, $selected }) =>
    $selected ? $token.colorPrimary : $token.colorBgContainer};
  border: 1.5px solid
    ${({ $token, $selected }) =>
      $selected ? $token.colorPrimary : $token.colorBorder};
`;

const StyledLabel = styled.div<{ $token: GlobalToken }>`
  font-weight: 600;
  font-size: 15px;
  color: ${({ $token }) => $token.colorText};
  margin-bottom: 4px;
`;

const StyledDescription = styled.div<{ $token: GlobalToken }>`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ $token }) => $token.colorTextSecondary};
`;
